<template>
  <div class="player-detail-page">
    <!-- Back & Header -->
    <div class="detail-header">
      <button class="btn btn-back" @click="goBack">{{ $t('admin.player_detail.back') }}</button>
      <div v-if="player" class="detail-header-info">
        <div class="player-avatar-lg">{{ getInitials(player.profile.username) }}</div>
        <div>
          <h1 class="page-title">{{ player.profile.username }}</h1>
          <p class="page-subtitle">{{ player.profile.email }}</p>
        </div>
        <span class="badge" :class="statusBadgeClass(player.profile.status)">{{ player.profile.status }}</span>
        <span class="badge" :class="kycBadgeClass(player.profile.kyc_status)">{{ player.profile.kyc_status }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <span class="spinner"></span>
      {{ $t('admin.player_detail.loading') }}
    </div>

    <!-- Error -->
    <div v-else-if="errorMsg" class="error-state">
      {{ errorMsg }}
    </div>

    <!-- Content -->
    <template v-else-if="player">
      <!-- Tab Navigation -->
      <div class="tab-nav">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="tab-btn"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >{{ tab.label }}</button>
      </div>

      <!-- Overview Tab -->
      <div v-if="activeTab === 'overview'" class="tab-content">
        <div class="overview-grid">
          <!-- Profile Card -->
          <div class="card">
            <h3 class="card-title">{{ $t('admin.player_detail.profile_info_title') }}</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_username') }}</span>
                <span class="info-value">{{ player.profile.username }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_email') }}</span>
                <span class="info-value">{{ player.profile.email }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_country') }}</span>
                <span class="info-value">{{ player.profile.country ?? 'N/A' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_language') }}</span>
                <span class="info-value">{{ player.profile.language ?? 'N/A' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_dob') }}</span>
                <span class="info-value">{{ player.profile.date_of_birth ?? 'N/A' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_currency') }}</span>
                <span class="info-value">{{ player.profile.preferred_currency ?? 'USD' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_account_status') }}</span>
                <span class="badge" :class="statusBadgeClass(player.profile.status)">{{ player.profile.status }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_kyc_status') }}</span>
                <span class="badge" :class="kycBadgeClass(player.profile.kyc_status)">{{ player.profile.kyc_status }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_vip_level') }}</span>
                <span class="info-value vip-badge">{{ vipLabel(player.profile.vip_level) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_vip_points') }}</span>
                <span class="info-value">{{ player.profile.vip_points?.toLocaleString() ?? '0' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_spendable_points') }}</span>
                <span class="info-value points-balance">{{ player.profile.spendable_points?.toLocaleString() ?? '0' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_registered') }}</span>
                <span class="info-value">{{ formatDate(player.profile.created_at) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_last_login') }}</span>
                <span class="info-value">{{ player.profile.last_login_at ? formatDate(player.profile.last_login_at) : $t('admin.player_detail.label_never') }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('admin.player_detail.label_last_login_ip') }}</span>
                <span class="info-value mono">{{ player.profile.last_login_ip ?? 'N/A' }}</span>
              </div>
            </div>
          </div>

          <!-- Financial Summary Card -->
          <div class="card">
            <h3 class="card-title">{{ $t('admin.player_detail.financial_summary_title') }}</h3>
            <div class="stat-grid">
              <div v-for="wallet in player.wallets" :key="wallet.id" class="wallet-stats">
                <h4 class="wallet-currency">{{ $t('admin.player_detail.wallet_currency_label', { currency: wallet.currency }) }}</h4>
                <div class="stat-row">
                  <span class="stat-label">{{ $t('admin.player_detail.label_balance') }}</span>
                  <span class="stat-value accent">{{ formatCents(wallet.balance) }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">{{ $t('admin.player_detail.label_bonus_balance') }}</span>
                  <span class="stat-value">{{ formatCents(wallet.bonus_balance) }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">{{ $t('admin.player_detail.label_locked_balance') }}</span>
                  <span class="stat-value">{{ formatCents(wallet.locked_balance) }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">{{ $t('admin.player_detail.label_total_deposited') }}</span>
                  <span class="stat-value success">{{ formatCents(wallet.total_deposited) }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">{{ $t('admin.player_detail.label_total_withdrawn') }}</span>
                  <span class="stat-value danger">{{ formatCents(wallet.total_withdrawn) }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">{{ $t('admin.player_detail.label_total_wagered') }}</span>
                  <span class="stat-value">{{ formatCents(wallet.total_wagered) }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">{{ $t('admin.player_detail.label_total_won') }}</span>
                  <span class="stat-value">{{ formatCents(wallet.total_won) }}</span>
                </div>
              </div>
              <p v-if="player.wallets.length === 0" class="no-data">{{ $t('admin.player_detail.no_wallets') }}</p>
            </div>
          </div>

          <!-- Risk Flags -->
          <div class="card">
            <h3 class="card-title">{{ $t('admin.player_detail.risk_flags_title') }}</h3>
            <div v-if="player.risk_flags.length === 0" class="no-data">{{ $t('admin.player_detail.no_risk_flags') }}</div>
            <div v-else class="risk-list">
              <div v-for="(flag, i) in player.risk_flags" :key="i" class="risk-item">
                <span class="risk-icon">&#x26A0;&#xFE0F;</span>
                <div>
                  <span class="risk-type">{{ formatRiskType(flag.type) }}</span>
                  <span class="risk-desc">{{ flag.description }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- IP History -->
          <div class="card">
            <h3 class="card-title">{{ $t('admin.player_detail.ip_history_title') }}</h3>
            <div v-if="player.ip_history.length === 0" class="no-data">{{ $t('admin.player_detail.no_ip_history') }}</div>
            <table v-else class="mini-table">
              <thead>
                <tr>
                  <th>{{ $t('admin.player_detail.col_ip_address') }}</th>
                  <th>{{ $t('admin.player_detail.col_date') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ip, i) in player.ip_history" :key="i">
                  <td class="mono">{{ ip.ip_address }}</td>
                  <td>{{ formatDate(ip.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Wallet Tab -->
      <div v-if="activeTab === 'wallet'" class="tab-content">
        <!-- Wallet Balances -->
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.wallet_balances_title') }}</h3>
          <div v-if="player.wallets.length === 0" class="no-data">{{ $t('admin.player_detail.no_wallets') }}</div>
          <div v-else class="wallets-grid">
            <div v-for="wallet in player.wallets" :key="wallet.id" class="wallet-card">
              <div class="wallet-card-header">
                <span class="wallet-card-currency">{{ wallet.currency }}</span>
              </div>
              <div class="wallet-card-balance">{{ formatCents(wallet.balance) }}</div>
              <div class="wallet-card-details">
                <div class="wallet-detail-row">
                  <span>{{ $t('admin.player_detail.wallet_bonus') }}</span>
                  <span>{{ formatCents(wallet.bonus_balance) }}</span>
                </div>
                <div class="wallet-detail-row">
                  <span>{{ $t('admin.player_detail.wallet_locked') }}</span>
                  <span>{{ formatCents(wallet.locked_balance) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Manual Credit/Debit Form -->
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.adjustment_title') }}</h3>
          <form class="adjustment-form" @submit.prevent="submitAdjustment">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">{{ $t('admin.player_detail.adjustment_type_label') }}</label>
                <select v-model="adjustmentForm.type" class="form-input">
                  <option value="credit">{{ $t('admin.player_detail.adjustment_type_credit') }}</option>
                  <option value="debit">{{ $t('admin.player_detail.adjustment_type_debit') }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('admin.player_detail.adjustment_balance_label') }}</label>
                <select v-model="adjustmentForm.balance_type" class="form-input">
                  <option value="cash">{{ $t('admin.player_detail.adjustment_balance_cash') }}</option>
                  <option value="bonus">{{ $t('admin.player_detail.adjustment_balance_bonus') }}</option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">{{ $t('admin.player_detail.adjustment_amount_label') }}</label>
                <input
                  v-model.number="adjustmentForm.amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="form-input"
                  placeholder="0.00"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('admin.player_detail.adjustment_reason_label') }}</label>
                <select v-model="adjustmentForm.reason" class="form-input">
                  <option value="">{{ $t('admin.player_detail.adjustment_reason_placeholder') }}</option>
                  <option value="Bonus award">{{ $t('admin.player_detail.adjustment_reason_bonus') }}</option>
                  <option value="Error correction">{{ $t('admin.player_detail.adjustment_reason_error') }}</option>
                  <option value="Customer goodwill">{{ $t('admin.player_detail.adjustment_reason_goodwill') }}</option>
                  <option value="Promotion credit">{{ $t('admin.player_detail.adjustment_reason_promo') }}</option>
                  <option value="Chargeback recovery">{{ $t('admin.player_detail.adjustment_reason_chargeback') }}</option>
                  <option value="Other">{{ $t('admin.player_detail.adjustment_reason_other') }}</option>
                </select>
              </div>
            </div>
            <div v-if="adjustmentForm.reason === 'Other'" class="form-row">
              <div class="form-group full-width">
                <label class="form-label">{{ $t('admin.player_detail.adjustment_custom_reason_label') }}</label>
                <input
                  v-model="adjustmentForm.customReason"
                  type="text"
                  class="form-input"
                  :placeholder="$t('admin.player_detail.adjustment_custom_reason_placeholder')"
                  maxlength="500"
                />
              </div>
            </div>
            <div class="form-actions">
              <button
                type="submit"
                class="btn"
                :class="adjustmentForm.type === 'credit' ? 'btn-success' : 'btn-danger'"
                :disabled="!isAdjustmentValid || adjustmentSubmitting"
              >
                {{ adjustmentSubmitting ? $t('common.processing') : (adjustmentForm.type === 'credit' ? $t('admin.player_detail.apply_credit') : $t('admin.player_detail.apply_debit')) }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Games Tab -->
      <div v-if="activeTab === 'games'" class="tab-content">
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.games_title') }}</h3>
          <div class="tab-filter-bar">
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.games_filter_game') }}</label>
              <input
                v-model="gamesFilters.game_slug"
                type="text"
                class="form-input filter-input"
                :placeholder="$t('admin.player_detail.games_filter_game_placeholder')"
                @keyup.enter="applyGamesFilters"
              />
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.games_filter_from') }}</label>
              <input v-model="gamesFilters.date_from" type="date" class="form-input filter-input" @change="applyGamesFilters" />
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.games_filter_to') }}</label>
              <input v-model="gamesFilters.date_to" type="date" class="form-input filter-input" @change="applyGamesFilters" />
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.games_filter_result') }}</label>
              <select v-model="gamesFilters.result" class="form-input filter-input" @change="applyGamesFilters">
                <option value="">{{ $t('admin.player_detail.games_filter_all') }}</option>
                <option value="win">{{ $t('admin.player_detail.games_filter_win') }}</option>
                <option value="loss">{{ $t('admin.player_detail.games_filter_loss') }}</option>
              </select>
            </div>
            <button class="btn btn-sm btn-secondary" @click="resetGamesFilters">{{ $t('admin.player_detail.games_reset') }}</button>
          </div>

          <div v-if="gamesLoading" class="tab-loading">
            <span class="spinner"></span> {{ $t('admin.player_detail.games_loading') }}
          </div>

          <template v-else-if="gamesData.length > 0">
            <div class="table-wrapper">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>{{ $t('admin.player_detail.col_date') }}</th>
                    <th>{{ $t('admin.player_detail.col_game') }}</th>
                    <th>{{ $t('admin.player_detail.col_bet') }}</th>
                    <th>{{ $t('admin.player_detail.col_payout') }}</th>
                    <th>{{ $t('admin.player_detail.col_multiplier') }}</th>
                    <th>{{ $t('admin.player_detail.col_result') }}</th>
                    <th>{{ $t('admin.player_detail.col_provably_fair') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="round in gamesData" :key="round.id">
                    <td>{{ formatDate(round.created_at) }}</td>
                    <td class="game-name">{{ round.game_name }}</td>
                    <td class="mono">{{ formatCents(round.bet_amount) }}</td>
                    <td class="mono" :class="round.win_amount > round.bet_amount ? 'text-success' : ''">{{ formatCents(round.win_amount) }}</td>
                    <td class="mono">{{ round.multiplier.toFixed(2) }}x</td>
                    <td><span class="badge" :class="roundResultClass(round)">{{ roundResultLabel(round) }}</span></td>
                    <td><a :href="'/provably-fair/' + round.id" class="btn-link" target="_blank">{{ $t('admin.player_detail.games_verify') }}</a></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="tab-pagination">
              <button class="pagination-btn" :disabled="gamesPage <= 1" @click="gamesPage--; fetchGameRounds()">&larr;</button>
              <button
                v-for="p in gamesPageRange"
                :key="p"
                class="pagination-btn"
                :class="{ active: p === gamesPage }"
                @click="gamesPage = p; fetchGameRounds()"
              >{{ p }}</button>
              <span class="pagination-info">{{ gamesPageInfo }}</span>
              <button class="pagination-btn" :disabled="gamesPage >= gamesTotalPages" @click="gamesPage++; fetchGameRounds()">&rarr;</button>
            </div>
          </template>

          <div v-else class="no-data">{{ $t('admin.player_detail.no_games') }}</div>
        </div>
      </div>

      <!-- Transactions Tab -->
      <div v-if="activeTab === 'transactions'" class="tab-content">
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.transactions_title') }}</h3>
          <div class="tab-filter-bar">
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.tx_filter_type_label') }}</label>
              <select v-model="txFilters.type" class="form-input filter-input" @change="applyTxFilters">
                <option value="">{{ $t('admin.player_detail.games_filter_all') }}</option>
                <option value="deposit">{{ $t('admin.player_detail.tx_type_deposit') }}</option>
                <option value="withdrawal">{{ $t('admin.player_detail.tx_type_withdrawal') }}</option>
                <option value="bet">{{ $t('admin.player_detail.tx_type_bet') }}</option>
                <option value="win">{{ $t('admin.player_detail.tx_type_win') }}</option>
                <option value="bonus">{{ $t('admin.player_detail.tx_type_bonus') }}</option>
                <option value="adjustment">{{ $t('admin.player_detail.tx_type_adjustment') }}</option>
              </select>
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.tx_filter_status_label') }}</label>
              <select v-model="txFilters.status" class="form-input filter-input" @change="applyTxFilters">
                <option value="">{{ $t('admin.player_detail.games_filter_all') }}</option>
                <option value="completed">{{ $t('admin.player_detail.tx_status_completed') }}</option>
                <option value="pending">{{ $t('admin.player_detail.tx_status_pending') }}</option>
                <option value="failed">{{ $t('admin.player_detail.tx_status_failed') }}</option>
                <option value="cancelled">{{ $t('admin.player_detail.tx_status_cancelled') }}</option>
              </select>
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.games_filter_from') }}</label>
              <input v-model="txFilters.date_from" type="date" class="form-input filter-input" @change="applyTxFilters" />
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.games_filter_to') }}</label>
              <input v-model="txFilters.date_to" type="date" class="form-input filter-input" @change="applyTxFilters" />
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.tx_filter_min_label') }}</label>
              <input
                v-model="txFilters.amount_min"
                type="number"
                step="0.01"
                min="0"
                class="form-input filter-input filter-input-sm"
                placeholder="0"
                @change="applyTxFilters"
              />
            </div>
            <div class="filter-group">
              <label class="filter-label">{{ $t('admin.player_detail.tx_filter_max_label') }}</label>
              <input
                v-model="txFilters.amount_max"
                type="number"
                step="0.01"
                min="0"
                class="form-input filter-input filter-input-sm"
                placeholder="0"
                @change="applyTxFilters"
              />
            </div>
            <button class="btn btn-sm btn-secondary" @click="resetTxFilters">{{ $t('admin.player_detail.games_reset') }}</button>
          </div>

          <div v-if="txLoading" class="tab-loading">
            <span class="spinner"></span> {{ $t('admin.player_detail.tx_loading') }}
          </div>

          <template v-else-if="txData.length > 0">
            <div class="table-wrapper">
              <table class="data-table">
                <thead>
                  <tr>
                    <th class="sortable" @click="toggleTxSort('created_at')">
                      {{ $t('admin.player_detail.col_date') }} {{ txSortIcon('created_at') }}
                    </th>
                    <th class="sortable" @click="toggleTxSort('type')">
                      {{ $t('admin.player_detail.col_type') }} {{ txSortIcon('type') }}
                    </th>
                    <th class="sortable" @click="toggleTxSort('amount')">
                      {{ $t('admin.player_detail.col_amount') }} {{ txSortIcon('amount') }}
                    </th>
                    <th>{{ $t('admin.player_detail.col_currency') }}</th>
                    <th>{{ $t('admin.player_detail.col_method') }}</th>
                    <th>{{ $t('admin.player_detail.col_status') }}</th>
                    <th>{{ $t('admin.player_detail.col_balance_after') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in txData" :key="tx.id">
                    <td>{{ formatDate(tx.created_at) }}</td>
                    <td><span class="badge" :class="txTypeBadgeClass(tx.type)">{{ tx.type }}</span></td>
                    <td class="mono" :class="tx.amount >= 0 ? 'text-success' : 'text-danger'">{{ formatCents(tx.amount) }}</td>
                    <td>{{ tx.currency }}</td>
                    <td>{{ tx.payment_method ?? '-' }}</td>
                    <td><span class="badge" :class="txStatusBadgeClass(tx.status)">{{ tx.status }}</span></td>
                    <td class="mono">{{ formatCents(tx.balance_after) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="tab-pagination">
              <button class="pagination-btn" :disabled="txPage <= 1" @click="txPage--; fetchTransactions()">&larr;</button>
              <button
                v-for="p in txPageRange"
                :key="p"
                class="pagination-btn"
                :class="{ active: p === txPage }"
                @click="txPage = p; fetchTransactions()"
              >{{ p }}</button>
              <span class="pagination-info">{{ txPageInfo }}</span>
              <button class="pagination-btn" :disabled="txPage >= txTotalPages" @click="txPage++; fetchTransactions()">&rarr;</button>
            </div>
          </template>

          <div v-else class="no-data">{{ $t('admin.player_detail.no_transactions') }}</div>
        </div>
      </div>

      <!-- Bonuses Tab -->
      <div v-if="activeTab === 'bonuses'" class="tab-content">
        <!-- Active Bonuses -->
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.bonuses_active_title') }}</h3>
          <div v-if="activeBonuses.length === 0" class="no-data">{{ $t('admin.player_detail.no_active_bonuses') }}</div>
          <div v-else class="bonus-cards">
            <div v-for="bonus in activeBonuses" :key="bonus.id" class="bonus-card">
              <div class="bonus-card-header">
                <div>
                  <span class="bonus-name">{{ bonus.bonus_name }}</span>
                  <span class="badge badge-bonus-type">{{ formatBonusType(bonus.bonus_type) }}</span>
                </div>
                <span class="badge badge-active">{{ $t('admin.player_detail.badge_active') }}</span>
              </div>
              <div class="bonus-card-body">
                <div class="bonus-stat">
                  <span class="bonus-stat-label">{{ $t('admin.player_detail.bonus_amount') }}</span>
                  <span class="bonus-stat-value">{{ formatCents(bonus.bonus_amount) }}</span>
                </div>
                <div class="bonus-stat">
                  <span class="bonus-stat-label">{{ $t('admin.player_detail.bonus_wagering_progress') }}</span>
                  <span class="bonus-stat-value">{{ formatCents(bonus.wagering_completed) }} / {{ formatCents(bonus.wagering_required) }}</span>
                </div>
                <div class="progress-bar-container">
                  <div class="progress-bar" :style="{ width: bonus.wagering_progress + '%' }"></div>
                  <span class="progress-label">{{ bonus.wagering_progress.toFixed(1) }}%</span>
                </div>
                <div v-if="bonus.expires_at" class="bonus-stat">
                  <span class="bonus-stat-label">{{ $t('admin.player_detail.bonus_expires') }}</span>
                  <span class="bonus-stat-value" :class="isExpiringSoon(bonus.expires_at) ? 'text-warning' : ''">{{ formatDate(bonus.expires_at) }}</span>
                </div>
                <div v-if="bonus.activated_at" class="bonus-stat">
                  <span class="bonus-stat-label">{{ $t('admin.player_detail.bonus_activated') }}</span>
                  <span class="bonus-stat-value">{{ formatDate(bonus.activated_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bonus History -->
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.bonuses_history_title') }}</h3>

          <div v-if="bonusHistoryLoading" class="tab-loading">
            <span class="spinner"></span> {{ $t('admin.player_detail.bonus_history_loading') }}
          </div>

          <template v-else-if="bonusHistory.length > 0">
            <div class="table-wrapper">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>{{ $t('admin.player_detail.col_bonus_name') }}</th>
                    <th>{{ $t('admin.player_detail.col_type') }}</th>
                    <th>{{ $t('admin.player_detail.col_amount') }}</th>
                    <th>{{ $t('admin.player_detail.col_wagering') }}</th>
                    <th>{{ $t('admin.player_detail.col_status') }}</th>
                    <th>{{ $t('admin.player_detail.col_completed') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="bonus in bonusHistory" :key="bonus.id">
                    <td class="bonus-history-name">{{ bonus.bonus_name }}</td>
                    <td><span class="badge badge-bonus-type">{{ formatBonusType(bonus.bonus_type) }}</span></td>
                    <td class="mono">{{ formatCents(bonus.bonus_amount) }}</td>
                    <td>
                      <div class="mini-progress-container">
                        <div class="mini-progress-bar" :style="{ width: bonus.wagering_progress + '%' }"></div>
                        <span class="mini-progress-label">{{ bonus.wagering_progress.toFixed(0) }}%</span>
                      </div>
                    </td>
                    <td><span class="badge" :class="bonusStatusBadgeClass(bonus.status)">{{ bonus.status }}</span></td>
                    <td>{{ formatDate(bonus.completed_at ?? bonus.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="tab-pagination">
              <button class="pagination-btn" :disabled="bonusHistoryPage <= 1" @click="bonusHistoryPage--; fetchBonusHistory()">&larr;</button>
              <button
                v-for="p in bonusHistoryPageRange"
                :key="p"
                class="pagination-btn"
                :class="{ active: p === bonusHistoryPage }"
                @click="bonusHistoryPage = p; fetchBonusHistory()"
              >{{ p }}</button>
              <span class="pagination-info">{{ bonusHistoryPageInfo }}</span>
              <button class="pagination-btn" :disabled="bonusHistoryPage >= bonusHistoryTotalPages" @click="bonusHistoryPage++; fetchBonusHistory()">&rarr;</button>
            </div>
          </template>

          <div v-else class="no-data">{{ $t('admin.player_detail.no_bonus_history') }}</div>
        </div>
      </div>

      <!-- Points Tab -->
      <div v-if="activeTab === 'points'" class="tab-content">
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.points_title') }}</h3>

          <div v-if="pointsLoading" class="tab-loading">
            <span class="spinner"></span> {{ $t('admin.player_detail.points_loading') }}
          </div>

          <template v-else-if="pointsData.length > 0">
            <div class="table-wrapper">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>{{ $t('admin.player_detail.col_date') }}</th>
                    <th>{{ $t('admin.player_detail.col_type') }}</th>
                    <th>{{ $t('admin.player_detail.col_points') }}</th>
                    <th>{{ $t('admin.player_detail.col_reason') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in pointsData" :key="tx.id">
                    <td>{{ formatDate(tx.created_at) }}</td>
                    <td><span class="badge" :class="tx.type === 'earned' ? 'badge-active' : 'badge-suspended'">{{ tx.type }}</span></td>
                    <td class="mono" :class="tx.type === 'earned' ? 'delta-earned' : 'delta-spent'">
                      {{ tx.type === 'earned' ? '+' : '-' }}{{ tx.points.toLocaleString() }}
                    </td>
                    <td>{{ tx.reason }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="tab-pagination">
              <button class="pagination-btn" :disabled="pointsPage <= 1" @click="pointsPage--; void fetchPointsHistory()">&larr;</button>
              <button
                v-for="p in pointsPageRange"
                :key="p"
                class="pagination-btn"
                :class="{ active: p === pointsPage }"
                @click="pointsPage = p; void fetchPointsHistory()"
              >{{ p }}</button>
              <span class="pagination-info">{{ pointsPageInfo }}</span>
              <button class="pagination-btn" :disabled="pointsPage >= pointsTotalPages" @click="pointsPage++; void fetchPointsHistory()">&rarr;</button>
            </div>
          </template>

          <div v-else class="no-data">{{ $t('admin.player_detail.no_points') }}</div>
        </div>
      </div>

      <!-- KYC Tab -->
      <div v-if="activeTab === 'kyc'" class="tab-content">
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.kyc_status_title') }}</h3>
          <div class="kyc-status-row">
            <div class="info-item">
              <span class="info-label">{{ $t('admin.player_detail.label_current_status') }}</span>
              <span class="badge" :class="kycBadgeClass(player.profile.kyc_status)">{{ player.profile.kyc_status }}</span>
            </div>
          </div>
        </div>
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.kyc_docs_title') }}</h3>
          <div class="placeholder-tab">
            <p class="placeholder-text">{{ $t('admin.player_detail.kyc_docs_placeholder') }}</p>
            <p class="placeholder-sub">{{ $t('admin.player_detail.kyc_docs_placeholder_sub') }}</p>
          </div>
        </div>
      </div>

      <!-- Notes Tab -->
      <div v-if="activeTab === 'notes'" class="tab-content">
        <!-- Add Note Form -->
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.notes_add_title') }}</h3>
          <form class="note-form" @submit.prevent="submitNote">
            <textarea
              v-model="noteContent"
              class="form-input note-textarea"
              :placeholder="$t('admin.player_detail.notes_placeholder')"
              maxlength="500"
              rows="3"
            ></textarea>
            <div class="note-form-footer">
              <span class="char-count" :class="{ 'char-limit': noteContent.length >= 480 }">{{ noteContent.length }}/500</span>
              <button
                type="submit"
                class="btn btn-success btn-sm"
                :disabled="!noteContent.trim() || noteSubmitting"
              >{{ noteSubmitting ? $t('admin.player_detail.notes_saving') : $t('admin.player_detail.notes_add_btn') }}</button>
            </div>
          </form>
        </div>

        <!-- Notes List -->
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.notes_timeline_title') }}</h3>

          <div v-if="notesLoading" class="tab-loading">
            <span class="spinner"></span> {{ $t('admin.player_detail.notes_loading') }}
          </div>

          <div v-else-if="notesData.length === 0" class="no-data">{{ $t('admin.player_detail.no_notes') }}</div>

          <div v-else class="notes-timeline">
            <div v-for="note in notesData" :key="note.id" class="note-item">
              <div class="note-header">
                <div class="note-author">
                  <div class="note-avatar">{{ getInitials(note.admin_name) }}</div>
                  <div>
                    <span class="note-author-name">{{ note.admin_name }}</span>
                    <span class="note-date">{{ formatDate(note.created_at) }}</span>
                  </div>
                </div>
                <button
                  v-if="canDeleteNote(note)"
                  class="btn-icon btn-icon-danger"
                  :title="$t('admin.player_detail.notes_delete_title')"
                  @click="confirmDeleteNote(note)"
                >&#x2715;</button>
              </div>
              <div class="note-body">{{ note.content }}</div>
            </div>
          </div>
        </div>

        <!-- Delete Note Confirmation Modal -->
        <div v-if="deleteNoteModal.visible" class="modal-overlay" @click.self="closeDeleteNoteModal">
          <div class="modal-content">
            <h3 class="modal-title">{{ $t('admin.player_detail.delete_note_modal_title') }}</h3>
            <p class="modal-text">{{ $t('admin.player_detail.delete_note_modal_text') }}</p>
            <div class="modal-preview">{{ deleteNoteModal.content }}</div>
            <div v-if="deleteNoteModal.error" class="modal-error">{{ deleteNoteModal.error }}</div>
            <div class="modal-actions">
              <button class="btn btn-secondary btn-sm" @click="closeDeleteNoteModal">{{ $t('common.cancel') }}</button>
              <button
                class="btn btn-danger btn-sm"
                :disabled="deleteNoteModal.loading"
                @click="executeDeleteNote"
              >{{ deleteNoteModal.loading ? $t('admin.player_detail.deleting') : $t('common.delete') }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Tab -->
      <div v-if="activeTab === 'actions'" class="tab-content">
        <div class="card">
          <h3 class="card-title">{{ $t('admin.player_detail.actions_title') }}</h3>
          <p class="actions-subtitle">{{ $t('admin.player_detail.actions_subtitle') }}</p>

          <div class="actions-grid">
            <!-- Status Actions -->
            <div class="action-card">
              <h4 class="action-card-title">{{ $t('admin.player_detail.account_status_title') }}</h4>
              <p class="action-card-desc">{{ $t('admin.player_detail.current_status') }} <span class="badge" :class="statusBadgeClass(player.profile.status)">{{ player.profile.status }}</span></p>
              <div class="action-card-buttons">
                <button
                  v-if="player.profile.status === 'active'"
                  class="btn btn-warning btn-sm"
                  @click="openActionModal('suspend')"
                >{{ $t('admin.player_detail.suspend_account') }}</button>
                <button
                  v-if="player.profile.status !== 'banned'"
                  class="btn btn-danger btn-sm"
                  @click="openActionModal('ban')"
                >{{ $t('admin.player_detail.ban_account') }}</button>
                <button
                  v-if="player.profile.status !== 'active'"
                  class="btn btn-success btn-sm"
                  @click="openActionModal('unsuspend')"
                >{{ $t('admin.player_detail.reactivate_account') }}</button>
              </div>
            </div>

            <!-- Security Actions -->
            <div class="action-card">
              <h4 class="action-card-title">{{ $t('admin.player_detail.security_title') }}</h4>
              <p class="action-card-desc">{{ $t('admin.player_detail.security_desc') }}</p>
              <div class="action-card-buttons">
                <button class="btn btn-secondary btn-sm" @click="openActionModal('force-logout')">{{ $t('admin.player_detail.force_logout') }}</button>
                <button class="btn btn-secondary btn-sm" @click="openActionModal('reset-password')">{{ $t('admin.player_detail.reset_password') }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Confirmation Modal -->
        <div v-if="actionModal.visible" class="modal-overlay" @click.self="closeActionModal">
          <div class="modal-content">
            <h3 class="modal-title">{{ actionModalTitle }}</h3>
            <p class="modal-text">{{ actionModalDescription }}</p>

            <!-- Reason field for suspend/ban -->
            <div v-if="actionModal.type === 'suspend' || actionModal.type === 'ban'" class="form-group" style="margin-top: 12px;">
              <label class="form-label">{{ $t('admin.player_detail.action_reason_label') }}</label>
              <textarea
                v-model="actionModal.reason"
                class="form-input note-textarea"
                :placeholder="$t('admin.player_detail.action_reason_placeholder')"
                rows="3"
                maxlength="500"
              ></textarea>
            </div>

            <div v-if="actionModal.error" class="modal-error">{{ actionModal.error }}</div>

            <div class="modal-actions">
              <button class="btn btn-secondary btn-sm" @click="closeActionModal">{{ $t('common.cancel') }}</button>
              <button
                class="btn btn-sm"
                :class="actionModalButtonClass"
                :disabled="actionModal.loading || !isActionValid"
                @click="executeAction"
              >{{ actionModal.loading ? $t('common.processing') : actionModalButtonLabel }}</button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useAdminStore } from '@/stores/admin'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const admin = useAdminStore()
const toast = useToastStore()

// ----------- Types -----------

interface PlayerProfile {
  id: number
  username: string
  email: string
  country: string | null
  language: string | null
  preferred_currency: string | null
  date_of_birth: string | null
  status: string
  kyc_status: string
  vip_level: number
  vip_points: number
  spendable_points: number
  last_login_at: string | null
  last_login_ip: string | null
  created_at: string | null
}

interface Wallet {
  id: number
  currency: string
  balance: number
  bonus_balance: number
  locked_balance: number
  total_deposited: number
  total_withdrawn: number
  total_wagered: number
  total_won: number
}

interface RiskFlag {
  type: string
  description: string
}

interface IpEntry {
  ip_address: string
  created_at: string | null
}

interface BonusEntry {
  id: number
  bonus_name: string
  bonus_type: string
  bonus_amount: number
  wagering_required: number
  wagering_completed: number
  wagering_progress: number
  status: string
  expires_at: string | null
  activated_at: string | null
  completed_at: string | null
  created_at: string | null
}

interface PlayerDetail {
  profile: PlayerProfile
  wallets: Wallet[]
  recent_transactions: unknown[]
  recent_game_rounds: unknown[]
  active_bonuses: BonusEntry[]
  risk_flags: RiskFlag[]
  notes_count: number
  ip_history: IpEntry[]
}

interface GameRound {
  id: number
  game_slug: string
  game_name: string
  bet_amount: number
  win_amount: number
  multiplier: number
  currency: string
  created_at: string
}

interface TransactionRow {
  id: number
  type: string
  amount: number
  currency: string
  status: string
  balance_before: number
  balance_after: number
  payment_method: string | null
  created_at: string
}

interface NoteEntry {
  id: number
  admin_name: string
  admin_user_id: number
  content: string
  created_at: string
}

interface PointsTransaction {
  id: number
  type: 'earned' | 'spent'
  points: number
  reason: string
  created_at: string
}

type ActionType = 'suspend' | 'ban' | 'unsuspend' | 'force-logout' | 'reset-password'

interface PaginatedResponse<T> {
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// ----------- Tab Config -----------

const tabs = computed(() => [
  { id: 'overview', label: t('admin.player_detail.tab_overview') },
  { id: 'wallet', label: t('admin.player_detail.tab_wallet') },
  { id: 'games', label: t('admin.player_detail.tab_games') },
  { id: 'transactions', label: t('admin.player_detail.tab_transactions') },
  { id: 'bonuses', label: t('admin.player_detail.tab_bonuses') },
  { id: 'points', label: t('admin.player_detail.tab_points') },
  { id: 'kyc', label: t('admin.player_detail.tab_kyc') },
  { id: 'notes', label: t('admin.player_detail.tab_notes') },
  { id: 'actions', label: t('admin.player_detail.tab_actions') },
])

// ----------- Core State -----------

const player = ref<PlayerDetail | null>(null)
const loading = ref(true)
const errorMsg = ref('')
const activeTab = ref('overview')

// Wallet adjustment form
const adjustmentForm = ref({
  type: 'credit' as 'credit' | 'debit',
  amount: null as number | null,
  reason: '',
  customReason: '',
  balance_type: 'cash',
})
const adjustmentSubmitting = ref(false)

// ----------- Games Tab State -----------

const gamesData = ref<GameRound[]>([])
const gamesPage = ref(1)
const gamesTotalPages = ref(1)
const gamesTotal = ref(0)
const gamesLoading = ref(false)
const gamesLoaded = ref(false)
const gamesFilters = ref({
  game_slug: '',
  date_from: '',
  date_to: '',
  result: '',
})

// ----------- Transactions Tab State -----------

const txData = ref<TransactionRow[]>([])
const txPage = ref(1)
const txTotalPages = ref(1)
const txTotal = ref(0)
const txLoading = ref(false)
const txLoaded = ref(false)
const txSort = ref({ field: 'created_at', direction: 'desc' })
const txFilters = ref({
  type: '',
  status: '',
  date_from: '',
  date_to: '',
  amount_min: '',
  amount_max: '',
})

// ----------- Bonuses Tab State -----------

const bonusHistory = ref<BonusEntry[]>([])
const bonusHistoryPage = ref(1)
const bonusHistoryTotalPages = ref(1)
const bonusHistoryTotal = ref(0)
const bonusHistoryLoading = ref(false)
const bonusHistoryLoaded = ref(false)

// ----------- Notes Tab State -----------

const notesData = ref<NoteEntry[]>([])
const notesLoading = ref(false)
const notesLoaded = ref(false)
const noteContent = ref('')
const noteSubmitting = ref(false)
const deleteNoteModal = ref({
  visible: false,
  noteId: 0,
  content: '',
  loading: false,
  error: '',
})

// ----------- Points Tab State -----------

const pointsData = ref<PointsTransaction[]>([])
const pointsPage = ref(1)
const pointsTotalPages = ref(1)
const pointsTotal = ref(0)
const pointsLoading = ref(false)
const pointsLoaded = ref(false)

// ----------- Actions Tab State -----------

const actionModal = ref({
  visible: false,
  type: '' as ActionType,
  reason: '',
  loading: false,
  error: '',
})

// ----------- Computed -----------

const playerId = computed(() => route.params.id as string)

const isAdjustmentValid = computed(() => {
  const form = adjustmentForm.value
  if (!form.amount || form.amount <= 0) return false
  if (!form.reason) return false
  if (form.reason === 'Other' && !form.customReason.trim()) return false
  return true
})

const activeBonuses = computed(() => {
  return player.value?.active_bonuses ?? []
})

// Action modal computed
const actionModalTitle = computed(() => {
  const titles: Record<ActionType, string> = {
    'suspend': t('admin.player_detail.action_modal_suspend_title'),
    'ban': t('admin.player_detail.action_modal_ban_title'),
    'unsuspend': t('admin.player_detail.action_modal_unsuspend_title'),
    'force-logout': t('admin.player_detail.action_modal_force_logout_title'),
    'reset-password': t('admin.player_detail.action_modal_reset_password_title'),
  }
  return titles[actionModal.value.type] ?? t('admin.player_detail.action_modal_default_title')
})

const actionModalDescription = computed(() => {
  const username = player.value?.profile.username ?? 'this player'
  const descs: Record<ActionType, string> = {
    'suspend': t('admin.player_detail.action_modal_suspend_desc', { username }),
    'ban': t('admin.player_detail.action_modal_ban_desc', { username }),
    'unsuspend': t('admin.player_detail.action_modal_unsuspend_desc', { username }),
    'force-logout': t('admin.player_detail.action_modal_force_logout_desc', { username }),
    'reset-password': t('admin.player_detail.action_modal_reset_password_desc', { username }),
  }
  return descs[actionModal.value.type] ?? ''
})

const actionModalButtonLabel = computed(() => {
  const labels: Record<ActionType, string> = {
    'suspend': t('admin.player_detail.action_btn_suspend'),
    'ban': t('admin.player_detail.action_btn_ban'),
    'unsuspend': t('admin.player_detail.action_btn_unsuspend'),
    'force-logout': t('admin.player_detail.action_btn_force_logout'),
    'reset-password': t('admin.player_detail.action_btn_reset_password'),
  }
  return labels[actionModal.value.type] ?? t('admin.player_detail.action_btn_confirm')
})

const actionModalButtonClass = computed(() => {
  const classes: Record<ActionType, string> = {
    'suspend': 'btn-warning',
    'ban': 'btn-danger',
    'unsuspend': 'btn-success',
    'force-logout': 'btn-secondary',
    'reset-password': 'btn-secondary',
  }
  return classes[actionModal.value.type] ?? 'btn-secondary'
})

const isActionValid = computed(() => {
  const t = actionModal.value.type
  if (t === 'suspend' || t === 'ban') {
    return actionModal.value.reason.trim().length > 0
  }
  return true
})

// Games pagination
const gamesPageRange = computed(() => buildPageRange(gamesPage.value, gamesTotalPages.value))
const gamesPageInfo = computed(() => {
  if (gamesTotal.value === 0) return ''
  const from = (gamesPage.value - 1) * 25 + 1
  const to = Math.min(gamesPage.value * 25, gamesTotal.value)
  return t('common.pagination_range', { from, to, total: gamesTotal.value })
})

// Transactions pagination
const txPageRange = computed(() => buildPageRange(txPage.value, txTotalPages.value))
const txPageInfo = computed(() => {
  if (txTotal.value === 0) return ''
  const from = (txPage.value - 1) * 25 + 1
  const to = Math.min(txPage.value * 25, txTotal.value)
  return t('common.pagination_range', { from, to, total: txTotal.value })
})

// Bonus history pagination
const bonusHistoryPageRange = computed(() => buildPageRange(bonusHistoryPage.value, bonusHistoryTotalPages.value))
const bonusHistoryPageInfo = computed(() => {
  if (bonusHistoryTotal.value === 0) return ''
  const from = (bonusHistoryPage.value - 1) * 25 + 1
  const to = Math.min(bonusHistoryPage.value * 25, bonusHistoryTotal.value)
  return t('common.pagination_range', { from, to, total: bonusHistoryTotal.value })
})

// Points pagination
const pointsPageRange = computed(() => buildPageRange(pointsPage.value, pointsTotalPages.value))
const pointsPageInfo = computed(() => {
  if (pointsTotal.value === 0) return ''
  const from = (pointsPage.value - 1) * 25 + 1
  const to = Math.min(pointsPage.value * 25, pointsTotal.value)
  return t('common.pagination_range', { from, to, total: pointsTotal.value })
})

// ----------- Data Fetching -----------

async function fetchPlayer(): Promise<void> {
  loading.value = true
  errorMsg.value = ''
  try {
    const response = await apiClient.get<PlayerDetail>(`/admin/api/players/${playerId.value}`)
    player.value = response.data
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number; data?: { message?: string } } }
    if (axiosErr.response?.status === 404) {
      errorMsg.value = t('admin.player_detail.error_not_found')
    } else {
      errorMsg.value = axiosErr.response?.data?.message ?? t('admin.player_detail.error_load')
    }
  } finally {
    loading.value = false
  }
}

async function fetchGameRounds(): Promise<void> {
  gamesLoading.value = true
  try {
    const params: Record<string, string | number> = { page: gamesPage.value }
    if (gamesFilters.value.game_slug) params.game_slug = gamesFilters.value.game_slug
    if (gamesFilters.value.date_from) params.date_from = gamesFilters.value.date_from
    if (gamesFilters.value.date_to) params.date_to = gamesFilters.value.date_to
    if (gamesFilters.value.result) params.result = gamesFilters.value.result

    const response = await apiClient.get<PaginatedResponse<GameRound>>(
      `/admin/api/players/${playerId.value}/game-rounds`,
      { params },
    )
    gamesData.value = response.data.data
    gamesTotalPages.value = response.data.last_page
    gamesTotal.value = response.data.total
    gamesLoaded.value = true
  } catch {
    toast.error(t('admin.player_detail.error_games'))
  } finally {
    gamesLoading.value = false
  }
}

async function fetchTransactions(): Promise<void> {
  txLoading.value = true
  try {
    const params: Record<string, string | number> = {
      page: txPage.value,
      sort: txSort.value.field,
      direction: txSort.value.direction,
    }
    if (txFilters.value.type) params.type = txFilters.value.type
    if (txFilters.value.status) params.status = txFilters.value.status
    if (txFilters.value.date_from) params.date_from = txFilters.value.date_from
    if (txFilters.value.date_to) params.date_to = txFilters.value.date_to
    if (txFilters.value.amount_min) params.amount_min = txFilters.value.amount_min
    if (txFilters.value.amount_max) params.amount_max = txFilters.value.amount_max

    const response = await apiClient.get<PaginatedResponse<TransactionRow>>(
      `/admin/api/players/${playerId.value}/transactions`,
      { params },
    )
    txData.value = response.data.data
    txTotalPages.value = response.data.last_page
    txTotal.value = response.data.total
    txLoaded.value = true
  } catch {
    toast.error(t('admin.player_detail.error_transactions'))
  } finally {
    txLoading.value = false
  }
}

async function fetchBonusHistory(): Promise<void> {
  bonusHistoryLoading.value = true
  try {
    const response = await apiClient.get<PaginatedResponse<BonusEntry>>(
      `/admin/api/players/${playerId.value}/bonuses`,
      { params: { status: 'history', page: bonusHistoryPage.value } },
    )
    bonusHistory.value = response.data.data
    bonusHistoryTotalPages.value = response.data.last_page
    bonusHistoryTotal.value = response.data.total
    bonusHistoryLoaded.value = true
  } catch {
    toast.error(t('admin.player_detail.error_bonus_history'))
  } finally {
    bonusHistoryLoading.value = false
  }
}

async function fetchPointsHistory(): Promise<void> {
  pointsLoading.value = true
  try {
    const response = await apiClient.get<PaginatedResponse<PointsTransaction>>(
      `/admin/api/players/${playerId.value}/points-history`,
      { params: { page: pointsPage.value } },
    )
    pointsData.value = response.data.data
    pointsTotalPages.value = response.data.last_page
    pointsTotal.value = response.data.total
    pointsLoaded.value = true
  } catch {
    toast.error(t('admin.player_detail.error_points'))
  } finally {
    pointsLoading.value = false
  }
}

async function fetchNotes(): Promise<void> {
  notesLoading.value = true
  try {
    const response = await apiClient.get<{ notes: NoteEntry[] }>(`/admin/api/players/${playerId.value}/notes`)
    notesData.value = response.data.notes
    notesLoaded.value = true
  } catch {
    toast.error(t('admin.player_detail.error_notes'))
  } finally {
    notesLoading.value = false
  }
}

// ----------- Notes Actions -----------

async function submitNote(): Promise<void> {
  if (!noteContent.value.trim() || noteSubmitting.value) return

  noteSubmitting.value = true
  try {
    await apiClient.post(`/admin/api/players/${playerId.value}/notes`, {
      content: noteContent.value.trim(),
    })
    noteContent.value = ''
    toast.success(t('admin.player_detail.toast_note_added'))
    await fetchNotes()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    toast.error(axiosErr.response?.data?.message ?? t('admin.player_detail.error_note_add'))
  } finally {
    noteSubmitting.value = false
  }
}

function canDeleteNote(note: NoteEntry): boolean {
  const currentAdmin = admin.user
  if (!currentAdmin) return false
  if (note.admin_user_id === currentAdmin.id) return true
  if (admin.hasRole(['super_admin'])) return true
  return false
}

function confirmDeleteNote(note: NoteEntry): void {
  deleteNoteModal.value = {
    visible: true,
    noteId: note.id,
    content: note.content.length > 100 ? note.content.slice(0, 100) + '...' : note.content,
    loading: false,
    error: '',
  }
}

function closeDeleteNoteModal(): void {
  deleteNoteModal.value.visible = false
}

async function executeDeleteNote(): Promise<void> {
  deleteNoteModal.value.loading = true
  deleteNoteModal.value.error = ''
  try {
    await apiClient.delete(`/admin/api/players/${playerId.value}/notes/${deleteNoteModal.value.noteId}`)
    toast.success(t('admin.player_detail.toast_note_deleted'))
    closeDeleteNoteModal()
    await fetchNotes()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    deleteNoteModal.value.error = axiosErr.response?.data?.message ?? t('admin.player_detail.error_note_delete')
  } finally {
    deleteNoteModal.value.loading = false
  }
}

// ----------- Account Actions -----------

function openActionModal(type: ActionType): void {
  actionModal.value = {
    visible: true,
    type,
    reason: '',
    loading: false,
    error: '',
  }
}

function closeActionModal(): void {
  actionModal.value.visible = false
}

async function executeAction(): Promise<void> {
  if (!isActionValid.value || !player.value) return

  actionModal.value.loading = true
  actionModal.value.error = ''

  const type = actionModal.value.type
  const endpoint = `/admin/api/players/${playerId.value}/${type}`

  try {
    const payload: Record<string, string> = {}
    if (type === 'suspend' || type === 'ban') {
      payload.reason = actionModal.value.reason.trim()
    }

    await apiClient.post(endpoint, payload)

    const messages: Record<ActionType, string> = {
      'suspend': t('admin.player_detail.toast_suspended'),
      'ban': t('admin.player_detail.toast_banned'),
      'unsuspend': t('admin.player_detail.toast_unsuspended'),
      'force-logout': t('admin.player_detail.toast_force_logout'),
      'reset-password': t('admin.player_detail.toast_reset_password'),
    }
    toast.success(messages[type])
    closeActionModal()

    // Refresh player data to update status
    await fetchPlayer()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    actionModal.value.error = axiosErr.response?.data?.message ?? t('admin.player_detail.error_action')
  } finally {
    actionModal.value.loading = false
  }
}

// ----------- Tab Filter Actions -----------

function applyGamesFilters(): void {
  gamesPage.value = 1
  fetchGameRounds()
}

function resetGamesFilters(): void {
  gamesFilters.value = { game_slug: '', date_from: '', date_to: '', result: '' }
  gamesPage.value = 1
  fetchGameRounds()
}

function applyTxFilters(): void {
  txPage.value = 1
  fetchTransactions()
}

function resetTxFilters(): void {
  txFilters.value = { type: '', status: '', date_from: '', date_to: '', amount_min: '', amount_max: '' }
  txSort.value = { field: 'created_at', direction: 'desc' }
  txPage.value = 1
  fetchTransactions()
}

function toggleTxSort(field: string): void {
  if (txSort.value.field === field) {
    txSort.value.direction = txSort.value.direction === 'asc' ? 'desc' : 'asc'
  } else {
    txSort.value = { field, direction: 'desc' }
  }
  txPage.value = 1
  fetchTransactions()
}

function txSortIcon(field: string): string {
  if (txSort.value.field !== field) return ''
  return txSort.value.direction === 'asc' ? '\u25B2' : '\u25BC'
}

// ----------- Wallet Adjustment -----------

async function submitAdjustment(): Promise<void> {
  if (!isAdjustmentValid.value || !player.value) return

  adjustmentSubmitting.value = true
  const form = adjustmentForm.value
  const reason = form.reason === 'Other' ? form.customReason.trim() : form.reason
  const endpoint = form.type === 'credit'
    ? `/admin/api/wallet/${player.value.profile.id}/credit`
    : `/admin/api/wallet/${player.value.profile.id}/debit`

  try {
    await apiClient.post(endpoint, {
      amount: form.amount,
      reason,
      balance_type: form.balance_type,
    })

    toast.success(form.type === 'credit'
      ? t('admin.player_detail.toast_adjustment_credit', { amount: form.amount?.toFixed(2) })
      : t('admin.player_detail.toast_adjustment_debit', { amount: form.amount?.toFixed(2) }))

    // Reset form
    adjustmentForm.value = {
      type: 'credit',
      amount: null,
      reason: '',
      customReason: '',
      balance_type: 'cash',
    }

    // Refresh player data to update balances
    await fetchPlayer()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    toast.error(axiosErr.response?.data?.message ?? t('admin.player_detail.error_adjustment'))
  } finally {
    adjustmentSubmitting.value = false
  }
}

// ----------- Formatting -----------

function formatCents(cents: number): string {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string | null): string {
  if (!iso) return 'N/A'
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function getInitials(username: string): string {
  return username
    .split(/[_.\-\s]/)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active': return 'badge-active'
    case 'suspended': return 'badge-suspended'
    case 'banned': return 'badge-suspended'
    default: return 'badge-pending'
  }
}

function kycBadgeClass(kycStatus: string): string {
  switch (kycStatus) {
    case 'verified': return 'badge-verified'
    case 'pending': return 'badge-pending-kyc'
    case 'unverified': return 'badge-unverified'
    case 'rejected': return 'badge-suspended'
    default: return 'badge-unverified'
  }
}

function vipLabel(level: number): string {
  const labels: Record<number, string> = {
    0: t('admin.player_detail.vip_standard'),
    1: t('common.vip.bronze'),
    2: t('common.vip.silver'),
    3: t('common.vip.gold'),
    4: t('common.vip.platinum'),
    5: t('common.vip.diamond'),
  }
  return labels[level] ?? `Level ${level}`
}

function formatRiskType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function roundResultClass(round: GameRound): string {
  if (round.win_amount > round.bet_amount) return 'badge-active'
  if (round.win_amount < round.bet_amount) return 'badge-suspended'
  return 'badge-pending'
}

function roundResultLabel(round: GameRound): string {
  if (round.win_amount > round.bet_amount) return t('admin.player_detail.result_win')
  if (round.win_amount < round.bet_amount) return t('admin.player_detail.result_loss')
  return t('admin.player_detail.result_push')
}

function txTypeBadgeClass(type: string): string {
  switch (type) {
    case 'deposit': return 'badge-active'
    case 'withdrawal': return 'badge-withdrawal'
    case 'bet': return 'badge-bet'
    case 'win': return 'badge-win'
    case 'bonus': return 'badge-bonus'
    default: return 'badge-pending'
  }
}

function txStatusBadgeClass(status: string): string {
  switch (status) {
    case 'completed': return 'badge-active'
    case 'pending': return 'badge-pending'
    case 'failed': return 'badge-suspended'
    case 'cancelled': return 'badge-suspended'
    default: return 'badge-pending'
  }
}

function formatBonusType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function bonusStatusBadgeClass(status: string): string {
  switch (status) {
    case 'completed': return 'badge-active'
    case 'forfeited': return 'badge-suspended'
    case 'expired': return 'badge-pending'
    case 'cancelled': return 'badge-suspended'
    default: return 'badge-pending'
  }
}

function isExpiringSoon(expiresAt: string): boolean {
  const diff = new Date(expiresAt).getTime() - Date.now()
  return diff > 0 && diff < 24 * 60 * 60 * 1000 // Less than 24 hours
}

// ----------- Pagination Helpers -----------

function buildPageRange(current: number, total: number): number[] {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const start = Math.max(1, current - 2)
  const end = Math.min(total, start + 4)
  const adjusted = Math.max(1, end - 4)
  return Array.from({ length: end - adjusted + 1 }, (_, i) => adjusted + i)
}

// ----------- Navigation -----------

function goBack() {
  router.push({ name: 'admin.players' })
}

// ----------- Watchers -----------

watch(activeTab, (newTab) => {
  if (newTab === 'games' && !gamesLoaded.value) {
    fetchGameRounds()
  }
  if (newTab === 'transactions' && !txLoaded.value) {
    fetchTransactions()
  }
  if (newTab === 'bonuses' && !bonusHistoryLoaded.value) {
    fetchBonusHistory()
  }
  if (newTab === 'points' && !pointsLoaded.value) {
    fetchPointsHistory()
  }
  if (newTab === 'notes' && !notesLoaded.value) {
    fetchNotes()
  }
})

// ----------- Lifecycle -----------

onMounted(() => {
  fetchPlayer()
})
</script>

<style scoped>
.player-detail-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Header */
.detail-header {
  margin-bottom: 24px;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-family: inherit;
  transition: all 150ms ease;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 16px;
}

.btn-back:hover {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.detail-header-info {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.player-avatar-lg {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1475e1, #9b59b6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 2px 0 0;
}

/* Tab Navigation */
.tab-nav {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 4px;
  overflow-x: auto;
}

.tab-btn {
  padding: 10px 18px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
  background: transparent;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
}

.tab-btn:hover {
  color: var(--text-primary, #fff);
  background: rgba(255, 255, 255, 0.05);
}

.tab-btn.active {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* Tab Content */
.tab-content {
  animation: fadeIn 0.2s ease both;
}

/* Cards */
.card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  margin-bottom: 16px;
  transition: border-color 150ms ease;
}

.card:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

/* Overview Grid */
.overview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.info-value {
  font-size: 13px;
  color: var(--text-primary, #fff);
}

.mono {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

.vip-badge {
  color: var(--accent-gold, #ffd700);
  font-weight: 600;
}

/* Stats */
.stat-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.wallet-stats {
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.wallet-currency {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 10px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.stat-row:last-child {
  border-bottom: none;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.stat-value {
  font-size: 13px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

.stat-value.accent {
  color: var(--accent-primary, #00e701);
  font-weight: 600;
}

.stat-value.success {
  color: var(--color-success, #00e701);
}

.stat-value.danger {
  color: var(--color-danger, #ed4245);
}

/* Risk Flags */
.risk-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.risk-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.15);
  border-radius: var(--radius-md, 8px);
}

.risk-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.risk-type {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-danger, #ed4245);
  display: block;
}

.risk-desc {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
}

/* IP Table */
.mini-table {
  width: 100%;
  border-collapse: collapse;
}

.mini-table th {
  padding: 8px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.mini-table td {
  padding: 10px 12px;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.mini-table tr:last-child td {
  border-bottom: none;
}

/* Wallet Tab */
.wallets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.wallet-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
  padding: 16px;
}

.wallet-card-header {
  margin-bottom: 8px;
}

.wallet-card-currency {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.wallet-card-balance {
  font-size: 22px;
  font-weight: 700;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--accent-primary, #00e701);
  margin-bottom: 12px;
}

.wallet-card-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wallet-detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.wallet-detail-row span:last-child {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-secondary, #b1bad3);
}

/* Adjustment Form */
.adjustment-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.form-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  transition: border-color 150ms ease;
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-input option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 7px 14px;
  font-size: 12px;
}

.btn-success {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-success:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 20px rgba(0, 231, 1, 0.3);
}

.btn-danger {
  background: var(--color-danger, #ed4245);
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: #d63638;
  box-shadow: 0 0 20px rgba(237, 66, 69, 0.3);
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.btn-link {
  background: none;
  border: none;
  color: var(--accent-secondary, #1475e1);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
  transition: color 150ms ease;
}

.btn-link:hover {
  color: #3b8de8;
  text-decoration: underline;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.badge-active {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-suspended {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-verified {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-unverified {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-pending-kyc {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

.badge-withdrawal {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-bet {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

.badge-win {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-bonus {
  background: rgba(255, 215, 0, 0.15);
  color: var(--accent-gold, #ffd700);
}

.badge-bonus-type {
  background: rgba(155, 89, 182, 0.15);
  color: #c39bd3;
}

/* Tab Filter Bar */
.tab-filter-bar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.filter-input {
  padding: 8px 10px;
  font-size: 12px;
  min-width: 120px;
}

.filter-input-sm {
  min-width: 80px;
  max-width: 100px;
}

/* Data Table */
.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background: rgba(255, 255, 255, 0.03);
}

.data-table th {
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

.data-table th.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease;
}

.data-table th.sortable:hover {
  color: var(--text-primary, #fff);
}

.data-table td {
  padding: 12px 14px;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  white-space: nowrap;
}

.data-table tbody tr {
  transition: background 150ms ease;
}

.data-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.game-name {
  font-weight: 500;
  color: var(--text-primary, #fff);
}

.bonus-history-name {
  font-weight: 500;
  color: var(--text-primary, #fff);
}

.text-success {
  color: var(--color-success, #00e701);
}

.text-danger {
  color: var(--color-danger, #ed4245);
}

.text-warning {
  color: var(--color-warning, #ffa726);
}

/* Tab Pagination */
.tab-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 8px;
}

/* Tab Loading */
.tab-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
}

/* Bonus Cards */
.bonus-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
}

.bonus-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
  padding: 16px;
}

.bonus-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.bonus-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  display: block;
  margin-bottom: 4px;
}

.bonus-card-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bonus-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bonus-stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.bonus-stat-value {
  font-size: 13px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

/* Progress Bar */
.progress-bar-container {
  position: relative;
  height: 20px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, var(--accent-primary, #00e701), #00cc01);
  border-radius: 10px;
  transition: width 300ms ease;
}

.progress-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Mini Progress (for history table) */
.mini-progress-container {
  position: relative;
  height: 16px;
  min-width: 80px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.mini-progress-bar {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, var(--accent-primary, #00e701), #00cc01);
  border-radius: 8px;
}

.mini-progress-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Loading / Error */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 64px 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state {
  text-align: center;
  padding: 64px 20px;
  color: var(--color-danger, #ed4245);
  font-size: 14px;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
}

/* Placeholder Tabs */
.placeholder-tab {
  text-align: center;
  padding: 48px 20px;
}

.placeholder-text {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.placeholder-sub {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 12px;
  margin-top: 6px;
}

/* KYC Tab */
.kyc-status-row {
  padding: 8px 0;
}

/* Notes Tab */
.note-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.note-textarea {
  resize: vertical;
  min-height: 72px;
  max-height: 200px;
  line-height: 1.5;
}

.note-form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.char-count {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.char-count.char-limit {
  color: var(--color-warning, #ffa726);
}

.notes-timeline {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.note-item {
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
  transition: border-color 150ms ease;
}

.note-item:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

.note-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.note-author {
  display: flex;
  align-items: center;
  gap: 10px;
}

.note-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1475e1, #9b59b6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.note-author-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  display: block;
}

.note-date {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  display: block;
  margin-top: 1px;
}

.note-body {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.btn-icon-danger {
  width: 26px;
  height: 26px;
  border-radius: var(--radius-md, 8px);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-icon-danger:hover {
  background: rgba(237, 66, 69, 0.15);
  border-color: rgba(237, 66, 69, 0.3);
  color: var(--color-danger, #ed4245);
}

/* Actions Tab */
.actions-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 20px;
}

.actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.action-card {
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
}

.action-card-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 8px;
}

.action-card-desc {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 14px;
  line-height: 1.5;
}

.action-card-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-warning {
  background: var(--color-warning, #ffa726);
  color: #000;
}

.btn-warning:hover:not(:disabled) {
  background: #e69520;
  box-shadow: 0 0 20px rgba(255, 167, 38, 0.3);
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.15s ease both;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  width: 440px;
  max-width: 90vw;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.2s ease both;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 8px;
}

.modal-text {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 12px;
  line-height: 1.5;
}

.modal-preview {
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: var(--radius-md, 8px);
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.modal-error {
  padding: 8px 12px;
  background: rgba(237, 66, 69, 0.1);
  border: 1px solid rgba(237, 66, 69, 0.2);
  border-radius: var(--radius-md, 8px);
  color: var(--color-danger, #ed4245);
  font-size: 12px;
  margin-bottom: 12px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

/* Responsive */
@media (max-width: 1200px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .tab-filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .bonus-cards {
    grid-template-columns: 1fr;
  }

  .actions-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .player-detail-page {
    padding: 16px;
  }

  .detail-header-info {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .tab-nav {
    overflow-x: auto;
  }
}

/* Points Tab */
.delta-earned {
  color: #4caf50;
  font-weight: 600;
}

.delta-spent {
  color: #ed4245;
  font-weight: 600;
}

.points-balance {
  color: #d4af37;
  font-weight: 600;
}
</style>
